// frontend/src/app/components/projects/project-corpus/dialogs/bibtex-form.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, inject, signal } from '@angular/core';
import { ProjectCorpusService } from '../../../../project-corpus/domain/project-corpus.service';
import { ToastService } from '../../../../services/toast.service';

/** Per-file size cap (100 MB), matching the design spec for all upload forms. */
const MAX_BIBTEX_BYTES = 100 * 1024 * 1024;

/**
 * BibTeX upload form for the Add-papers wizard.
 *
 * - Drag-or-click drop zone, single `.bib` file.
 * - After selecting, shows filename + size.
 * - Submitting calls `service.createImportJob({ source: 'BIBTEX', file })`
 *   and emits `done` so the wizard parent can close.
 *
 * Responsive behaviour:
 *  - Drop zone is full-width on every viewport.
 *  - Footer is `flex-col` on mobile and `sm:flex-row` on desktop so the
 *    submit button is full-width on phones.
 */
@Component({
  selector: 'app-bibtex-form',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bibtex-form.component.html',
  styleUrls: ['./bibtex-form.component.scss'],
})
export class BibtexFormComponent {
  private readonly service = inject(ProjectCorpusService);
  private readonly toast = inject(ToastService);

  readonly file = signal<File | null>(null);
  readonly fileError = signal<string | null>(null);
  readonly dragOver = signal(false);
  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);

  @Output() done = new EventEmitter<void>();

  onPick(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    this.handleSelection(files);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(false);
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    this.handleSelection(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver.set(false);
  }

  clearFile(): void {
    this.file.set(null);
    this.fileError.set(null);
  }

  submit(): void {
    if (this.submitting()) return;
    const f = this.file();
    if (!f) return;
    this.submitting.set(true);
    this.submitError.set(null);
    this.service.createImportJob({ source: 'BIBTEX', file: f }).subscribe({
      next: () => {
        this.submitting.set(false);
        this.toast.success('BibTeX import started', 'Your .bib file is being imported.');
        this.done.emit();
      },
      error: (err: unknown) => {
        this.submitError.set(this.toMessage(err));
        this.submitting.set(false);
      },
    });
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private handleSelection(files: File[]): void {
    this.fileError.set(null);
    if (files.length === 0) return;
    const f = files[0];
    if (f.size > MAX_BIBTEX_BYTES) {
      this.fileError.set(`${f.name} is larger than 100 MB and was not accepted.`);
      return;
    }
    if (!f.name.toLowerCase().endsWith('.bib')) {
      this.fileError.set('Only .bib files are accepted.');
      return;
    }
    this.file.set(f);
  }

  private toMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
  }
}
